import React, { useState, useContext, useCallback, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useForm } from "shared/hooks/form-hook";
import { StoreContext } from "shared/context/store-context";
import { isActiveOptions } from "shared/util/types";

import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import CustomSelect from "shared/components/FormElements/Select";
import MapWithTransferTimesContainer from "components/shared/MapWithTransferTimesContainer";
import { usePersonelServiceBackend } from "shared/hooks/backend/personel-service-hook";
import DeleteModal from "components/shared/DeleteModal";
import MessageModal from "components/shared/MessageModal";
import AutoAssignButton from "features/driver-scheduler/AutoAssignButton";
import { SERVICE_COMPLETION_STATUS_TYPES_TR, SERVICE_COMPLETION_STATUS_TYPES_EN } from "shared/utility/types";
import { getCurrentLanguage } from "shared/utility/lang";

import { VALIDATOR_REQUIRE, VALIDATOR_TIME_EMPTY_ACCEPTED } from "shared/util/validators";
import { checkIsPickUp } from "shared/utility/types";

const getUserLocation = (user, service) => {
  if (!user) return { lat: 0, lng: 0 };
  if (checkIsPickUp(service?.type || service?.routeType)) {
    return user.locationgeton || user.location || { lat: 0, lng: 0 };
  }
  return user.locationgetoff || user.location || { lat: 0, lng: 0 };
};

const ServiceItem = (props) => {
  const { t } = useTranslation();
  const { driverOptions, UpdatePersonelService, DeletePersonelService, drivers, transferregionOptions } =
    useContext(StoreContext);

  const currentLanguage = getCurrentLanguage();
  const serviceCompletionStatusTypes =
    currentLanguage === "tr" ? SERVICE_COMPLETION_STATUS_TYPES_TR : SERVICE_COMPLETION_STATUS_TYPES_EN;

  const [isProcessing, setIsProcessing] = useState(false);

  const [plateKey, setPlateKey] = useState(null);

  // Otomatik Sürücü Öner: atama sonrası SADECE bu satırın sürücü görünümünü tazelemek için.
  const [autoAssignTick, setAutoAssignTick] = useState(0);
  const [assignedDriverIds, setAssignedDriverIds] = useState(null);

  const { updatePersonelService } = usePersonelServiceBackend();

  const [showMap, setShowMap] = useState(false);

  const [currentUsers, setCurrentUsers] = useState(() =>
    props.users.map((user) => ({
      ...user,
      transferTime:
        props.service?.usersdetails?.find((ud) => ud?.user?.toString() === user?.id?.toString())?.transfertime || null,
    })),
  );

  useEffect(() => {
    setCurrentUsers(
      props.users.map((user) => ({
        ...user,
        transferTime:
          props.service?.usersdetails?.find((ud) => ud?.user?.toString() === user?.id?.toString())?.transfertime || null,
      })),
    );
  }, [props.users, props.service]);

  const memoizedStops = useMemo(() => {
    return currentUsers.map((user) => ({
      ...user,
      location: getUserLocation(user, props.service),
    }));
  }, [currentUsers, props.service]);

  const handleOptimize = useCallback((newStops) => {
    setCurrentUsers(newStops);
  }, []);

  const ItemType = "personelservice";
  const endPointDelete = "personelservices";

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteBlockedModal, setShowDeleteBlockedModal] = useState(false);

  const showDeleteWarningHandler = () => {
    if (props.service?.users?.length > 0) {
      setShowDeleteBlockedModal(true);
      return;
    }
    setShowConfirmModal(true);
  };

  const [formStateItem, inputHandlerItem] = useForm(
    {
      name: { value: "", isValid: true },
      drivers: { value: [], isValid: true },
      plate: { value: props.service.plate, isValid: true },
      starttime: { value: "00:01", isValid: true },
      stoptime: { value: "23:59", isValid: true },
      transferregion: { value: props?.service?.transferregion, isValid: true },
      routeClientArrivalTime: { value: props.service.routeClientArrivalTime, isValid: true },
      isActive: { value: null, isValid: true },
      completionStatus: { value: props.service.completionStatus, isValid: true },
    },
    true
  );

  const itemUpdateSubmitHandler = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsProcessing(true);

      let usersdetails = [];
      for (let i = 0; i < currentUsers.length; i++) {
        if (currentUsers[i].transferTime) {
          usersdetails.push({
            user: currentUsers[i].id,
            transfertime: currentUsers[i].transferTime,
          });
        }
      }

      // Harita açıksa toplam mesafe/süreyi ekrandan oku. Bu satır bir LİSTE içinde olduğu için
      // id'ler servise göre benzersizdir (idSuffix={`-${service.id}`}). Update (PATCH) yolu olduğundan
      // yalnızca geçerli sayı okunduğunda gönderilir (aksi halde iyi değer 0'a çekilebilir).
      let totalDistance;
      let totalDuration;
      try {
        const distRaw = document.getElementById(`totalDistance-${props.service.id}`)?.textContent?.replace("km", "").trim();
        const distNum = parseFloat(distRaw);
        totalDistance = Number.isFinite(distNum) ? distNum : undefined;
        const durNum = parseInt(document.getElementById(`totalDuration-${props.service.id}`)?.textContent, 10);
        totalDuration = Number.isFinite(durNum) ? durNum : undefined;
      } catch (err) {
        totalDistance = undefined;
        totalDuration = undefined;
      }

      const infoToBeSent = {
        name: formStateItem.inputs.name.value,
        drivers: formStateItem.inputs.drivers.value,
        plate: formStateItem.inputs.plate.value,
        starttime: formStateItem.inputs.starttime.value,
        stoptime: formStateItem.inputs.stoptime.value,
        transferregion: formStateItem.inputs.transferregion.value,
        routeClientArrivalTime: formStateItem.inputs.routeClientArrivalTime.value,
        isActive: formStateItem.inputs.isActive.value,
        completionStatus: formStateItem.inputs.completionStatus.value,
        users: currentUsers.map((user) => user.id),
        usersdetails: usersdetails,
        ...(totalDistance !== undefined ? { totalDistance } : {}),
        ...(totalDuration !== undefined ? { totalDuration } : {}),
      };

      const personelService = await updatePersonelService(props.service.id, infoToBeSent);

      if (personelService) {
        UpdatePersonelService(personelService);
        window.toast.success(t("updateSuccess"));
      }
      setIsProcessing(false);
    },
    [
      formStateItem.inputs.name.value,
      formStateItem.inputs.drivers.value,
      formStateItem.inputs.plate.value,
      formStateItem.inputs.starttime.value,
      formStateItem.inputs.stoptime.value,
      formStateItem.inputs.routeClientArrivalTime.value,
      formStateItem.inputs.transferregion.value,
      formStateItem.inputs.isActive.value,
      formStateItem.inputs.completionStatus.value,
      currentUsers,
      updatePersonelService,
      props.service.id,
      UpdatePersonelService,
      t,
    ]
  );

  const toggleShowMap = () => {
    setShowMap(!showMap);
  };

  const isDriverChangeReady = useRef(false);

  useEffect(() => {
    // prevent the initial CustomSelect fireChange from running business logic
    isDriverChangeReady.current = true;
  }, []);

  const driverChangeHandler = (selectedDrivers) => {
    if (!isDriverChangeReady.current) {
      inputHandlerItem("plate", formStateItem.inputs.plate.value, true);
      setPlateKey(formStateItem.inputs.plate.value);
      return;
    }

    if (selectedDrivers.length > 0) {
      const currentDriver = drivers.find((driver) => driver.id === selectedDrivers[0]);
      const currentPlate = currentDriver?.plate || "-";
      inputHandlerItem("plate", currentPlate, true);
      setPlateKey(currentPlate);
    } else if (formStateItem.inputs.plate.value !== null) {
      inputHandlerItem("plate", formStateItem.inputs.plate.value, true);
      setPlateKey(formStateItem.inputs.plate.value);
    } else {
      inputHandlerItem("plate", null, false);
      setPlateKey(null);
    }
  };

  // Sürücü select'inde gösterilecek seçili id'ler.
  // Auto-assign sonrası yerel normalize edilmiş id'lerden, aksi halde servis verisinden okunur.
  const selectedDriverIds = useMemo(() => {
    const source =
      assignedDriverIds ??
      (props.service?.drivers || []).map((d) => (d && typeof d === "object" ? d.id ?? d._id : d));
    return (source || []).filter(Boolean).map((x) => String(x));
  }, [assignedDriverIds, props.service]);

  // Otomatik Sürücü Öner modalından atama döndüğünde: YALNIZCA bu satırı güncelle.
  const handleAutoAssigned = useCallback(
    (updatedService) => {
      if (!updatedService) return;
      const updatedId = String(updatedService.id || updatedService._id || "");
      // GÜVENLİK: farklı bir servise ait callback ise hiçbir şey yapma.
      if (!updatedId || updatedId !== String(props.service.id)) return;

      const driverIds = (updatedService.drivers || []).map((d) =>
        d && typeof d === "object" ? String(d.id || d._id) : String(d),
      );

      // Bu satırın form state'i — sonradan "Güncelle"ye basılırsa doğru sürücü gönderilsin.
      inputHandlerItem("drivers", driverIds, true);

      // Plaka: ilk sürücünün plakası (manuel sürücü seçimindeki davranışla aynı).
      if (driverIds.length > 0) {
        const currentDriver = drivers.find((driver) => String(driver.id) === driverIds[0]);
        const currentPlate = currentDriver?.plate || "-";
        inputHandlerItem("plate", currentPlate, true);
        setPlateKey(currentPlate);
      }

      // Görünümü tazele (yalnızca bu satır).
      setAssignedDriverIds(driverIds);

      // Global store — ham backend yanıtını DEĞİL, mevcut servis shape'ini koruyan güvenli
      // bir merge yaz. Yalnızca drivers + assignment değişir; id ve diğer alanlar korunur.
      const newDrivers = driverIds.map((did) => drivers.find((dr) => String(dr.id) === did)).filter(Boolean);
      const mergedService = {
        ...props.service,
        drivers: newDrivers.length > 0 ? newDrivers : props.service.drivers,
        ...(updatedService.assignment ? { assignment: updatedService.assignment } : {}),
      };
      UpdatePersonelService(mergedService);

      setAutoAssignTick((n) => n + 1);
    },
    [props.service, drivers, inputHandlerItem, UpdatePersonelService],
  );

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={ItemType}
        itemId={props.service.id}
        onDeleted={async () => {
          setIsProcessing(true);
          await DeletePersonelService(props.service.id);
          setIsProcessing(false);
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />
      <MessageModal
        showModal={showDeleteBlockedModal}
        title={t("transferAnalyzer.services.deleteBlockedTitle")}
        text={t("transferAnalyzer.services.deleteBlockedHasPassengers")}
        onClose={() => setShowDeleteBlockedModal(false)}
      />
      <form key={props.service.id} onSubmit={itemUpdateSubmitHandler}>
        <div
          className="horizontal-flex"
          style={{ columnGap: "20px", rowGap: "0px", justifyContent: "flex-start", marginTop: "10px", width: "100%" }}
        >
          <Input
            id="name"
            element="input"
            type="text"
            label={t("transferServiceRow.nameLabel")}
            validators={[VALIDATOR_REQUIRE()]}
            errorText={t("transferServiceRow.nameError")}
            initialValue={props.service.name}
            initialValid={true}
            onInput={inputHandlerItem}
            dontShowError={true}
          />

          <CustomSelect
            key={`drivers-${props.service.id}-${autoAssignTick}`}
            options={driverOptions}
            id="drivers"
            isMulti={true}
            onInput={inputHandlerItem}
            label={t("transferServiceRow.driversLabel")}
            initialValue={driverOptions.filter((option) => selectedDriverIds.includes(String(option.value)))}
            initialValid={true}
            fireChange={driverChangeHandler}
          ></CustomSelect>

          <div className="horizontal-flex" style={{ width: "200px" }}>
            <CustomSelect
              options={transferregionOptions}
              id="transferregion"
              onInput={inputHandlerItem}
              label={t("transferregion.title")}
              initialValue={
                transferregionOptions.find((option) => option.value === props.service?.transferregion) || null
              }
              initialValid={true}
            ></CustomSelect>
          </div>

          <div className="horizontal-flex" style={{ width: "600px" }}>
            <Input
              id="routeClientArrivalTime"
              element="input"
              type="text"
              label={t("transferServiceRow.arrivalLabel")}
              validators={[VALIDATOR_TIME_EMPTY_ACCEPTED()]}
              errorText={t("transferServiceRow.timeError")}
              onInput={inputHandlerItem}
              initialValue={props.service.routeClientArrivalTime || ""}
              initialValid={true}
              dontShowError={true}
            />

            <Input
              id="plate"
              key={plateKey}
              element="input"
              type="text"
              label={t("transferServiceRow.plateLabel")}
              validators={[VALIDATOR_REQUIRE()]}
              errorText={t("transferServiceRow.plateError")}
              onInput={inputHandlerItem}
              initialValue={plateKey}
              initialValid={Boolean(plateKey)}
              dontShowError={true}
            />

            <CustomSelect
              options={serviceCompletionStatusTypes}
              id="completionStatus"
              onInput={inputHandlerItem}
              label={t("completionStatus")}
              initialValue={serviceCompletionStatusTypes.find(
                (option) => option.value.toString() === props.service?.completionStatus.toString()
              )}
              initialValid={true}
            ></CustomSelect>

            <CustomSelect
              options={isActiveOptions}
              id="isActive"
              onInput={inputHandlerItem}
              label={t("status")}
              initialValue={isActiveOptions.find(
                (option) => option.value.toString() === props.service?.isActive.toString()
              )}
              validators={[VALIDATOR_REQUIRE()]}
              initialValid={true}
              disabled={true}
            ></CustomSelect>
          </div>

          <div
            className="horizontal-flex"
            style={{ gap: "10px", justifyContent: "space-between", alignItems: "center", flexWrap: "nowrap" }}
          >
            <div className="horizontal-flex" style={{ gap: "10px", justifyContent: "flex-start", alignItems: "center" }}>
              <Button type="button" danger o onClick={toggleShowMap} disabled={isProcessing}>
                {showMap ? t("hideMap") : t("showMap")}
              </Button>

              <Button type="submit" disabled={!formStateItem.isValid || isProcessing}>
                {t("update")}
              </Button>

              <AutoAssignButton loadedItem={props.service} isNewItem={false} onAssigned={handleAutoAssigned} />
            </div>

            <div className="horizontal-flex" style={{ gap: "10px", justifyContent: "flex-end" }}>
              <Button type="button" danger o onClick={showDeleteWarningHandler} disabled={isProcessing}>
                {t("delete")}
              </Button>
            </div>
          </div>
        </div>

        {showMap && (
          <div style={{ display: "flex", flexDirection: "row", columnGap: "10px" }}>
            <MapWithTransferTimesContainer
              stops={memoizedStops}
              origin={props.school}
              type={props.service.type || props.service.routeType || props.type}
              enablePolyline={true}
              transferStartFinishTime={props.service.routeClientArrivalTime}
              idSuffix={`-${props.service.id}`}
              onOptimize={handleOptimize}
            />
          </div>
        )}
      </form>
    </React.Fragment>
  );
};

export default ServiceItem;
